/**
 * Tenant identity + white-label seam — forked from vCIP_droom_4.0/src/theme/servedTenant.ts.
 *
 * DEC-092 (contract v0.1.9 §D): the exchange `summary` carries the tenant NAME and, on a be ≥ v0.1.9, the
 * platform `branding` block ({name, brandColor, brandColorStrong, brandContrast, logoUrl, cobrand}). The block
 * is parsed by the same https-only / real-hex sanitiser the droom pins, and `deriveBrandTokens`
 * (theme/brand.ts, copied verbatim) writes the five `--brand*` tokens inline on the `.droom` root. When the
 * block is absent or carries nothing usable the slot stays on the design-system default (no hex is written
 * here — the package owns its default) and the logo band shows the `--brand` monogram. Nothing here is
 * per-tenant: ONE universal app, skinned from the wire.
 */

import type { ExchangeSummary } from '@/api/client';
import { deriveBrandTokens, type BrandTokens } from './brand';

export interface TenantBranding {
  readonly brandColor?: string;
  readonly brandColorStrong?: string;
  readonly brandContrast?: string;
  readonly logoUrl: string | null;
  readonly initial?: string;
  /** The tenant's co-brand line, rendered as-is where the chrome has a cobrand slot; never invented. */
  readonly cobrand?: string;
}

export interface TenantConfig {
  readonly id: string;
  readonly name: string;
  readonly branding: TenantBranding;
}

const HEX_COLOUR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function asString(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined;
}
function asHexColour(v: unknown): string | undefined {
  const s = asString(v);
  return s !== undefined && HEX_COLOUR.test(s) ? s : undefined;
}
function asHttpsUrl(v: unknown): string | undefined {
  const s = asString(v);
  if (s === undefined) return undefined;
  try {
    const url = new URL(s);
    return url.protocol === 'https:' ? url.toString() : undefined;
  } catch {
    return undefined;
  }
}

export function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'tenant';
}

/** Sanitise an OPTIONAL served branding block; null when absent or nothing usable. */
export function parseServedBranding(raw: unknown): TenantBranding | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const brandColor = asHexColour(r.brandColor);
  const brandColorStrong = asHexColour(r.brandColorStrong);
  const brandContrast = asHexColour(r.brandContrast);
  const logoUrl = asHttpsUrl(r.logoUrl);
  const initial = asString(r.initial)?.slice(0, 2);
  const cobrand = asString(r.cobrand)?.slice(0, 120);
  if (brandColor === undefined && logoUrl === undefined && cobrand === undefined) return null;
  return {
    ...(brandColor !== undefined ? { brandColor } : {}),
    ...(brandColorStrong !== undefined ? { brandColorStrong } : {}),
    ...(brandContrast !== undefined ? { brandContrast } : {}),
    logoUrl: logoUrl ?? null,
    ...(initial !== undefined ? { initial } : {}),
    ...(cobrand !== undefined ? { cobrand } : {}),
  };
}

export function tenantFrom(name: string | null | undefined, served: unknown = null): TenantConfig {
  const n = asString(name) ?? '';
  const branding = parseServedBranding(served) ?? { logoUrl: null };
  return { id: slugify(n || 'tenant'), name: n, branding };
}

/**
 * The ONE way a screen derives its tenant from the exchange summary: the summary's `tenantName` (the name the
 * consent copy already uses) + the served `branding` block. A null summary is the neutral, unbranded shell.
 */
export function tenantFromSummary(summary: Pick<ExchangeSummary, 'tenantName' | 'branding'> | null | undefined): TenantConfig | null {
  if (!summary) return null;
  const name = asString(summary.tenantName) ?? asString(summary.branding?.name) ?? '';
  return tenantFrom(name, summary.branding ?? null);
}

/** Inline `--brand*` tokens for the `.droom` root, or undefined to keep the package default. */
export function brandTokensFor(tenant: TenantConfig): BrandTokens | undefined {
  const b = tenant.branding;
  if (!b.brandColor) return undefined;
  return deriveBrandTokens({
    brandColor: b.brandColor,
    ...(b.brandColorStrong !== undefined ? { brandColorStrong: b.brandColorStrong } : {}),
    ...(b.brandContrast !== undefined ? { brandContrast: b.brandContrast } : {}),
  });
}
